import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ReviewsPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-28 md:pt-32 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                {t("sectionReview")}
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto">
                {t("sectionReviewDesc")}
              </p>
            </div>

            <div className="space-y-8">
              <div className="p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">What Is AllChinaBuy?</h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  AllChinaBuy is a shopping agent platform that helps international buyers purchase products from Chinese online marketplaces. The platform acts as an intermediary, handling communication with sellers, quality control inspections, warehouse storage, and international shipping logistics.
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  This review is an independent assessment based on publicly available information, user reports, and platform features. We are not affiliated with AllChinaBuy and this review does not constitute an endorsement.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-thumb-up-line text-accent-500" /> Pros
                  </h3>
                  <ul className="space-y-2 text-sm text-foreground-700">
                    <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5 shrink-0" /> Curated spreadsheet of product finds</li>
                    <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5 shrink-0" /> Quality control inspection service</li>
                    <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5 shrink-0" /> Multiple shipping options available</li>
                    <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5 shrink-0" /> Warehouse storage and consolidation</li>
                    <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5 shrink-0" /> Community-driven product recommendations</li>
                  </ul>
                </div>
                <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-thumb-down-line text-primary-500" /> Cons
                  </h3>
                  <ul className="space-y-2 text-sm text-foreground-700">
                    <li className="flex items-start gap-2"><i className="ri-subtract-line text-primary-500 mt-0.5 shrink-0" /> Shipping costs can be unpredictable</li>
                    <li className="flex items-start gap-2"><i className="ri-subtract-line text-primary-500 mt-0.5 shrink-0" /> Customs and import duties not included</li>
                    <li className="flex items-start gap-2"><i className="ri-subtract-line text-primary-500 mt-0.5 shrink-0" /> Language barrier with some sellers</li>
                    <li className="flex items-start gap-2"><i className="ri-subtract-line text-primary-500 mt-0.5 shrink-0" /> Resolution times for disputes can be lengthy</li>
                    <li className="flex items-start gap-2"><i className="ri-subtract-line text-primary-500 mt-0.5 shrink-0" /> Product availability changes frequently</li>
                  </ul>
                </div>
              </div>

              <div className="p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">How It Works</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { step: "1", title: "Browse & Select", desc: "Find products through the spreadsheet or marketplace listings." },
                    { step: "2", title: "Submit Order", desc: "AllChinaBuy purchases items on your behalf from the seller." },
                    { step: "3", title: "QC Inspection", desc: "Items arrive at warehouse and are inspected for quality." },
                    { step: "4", title: "Approve & Ship", desc: "Review QC photos, approve items, and choose shipping method." },
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-3 p-4 rounded-xl bg-background-50">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0 font-heading font-bold text-primary-600 text-sm">
                        {item.step}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-foreground-900 mb-1">{item.title}</h4>
                        <p className="text-xs text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 md:p-8 rounded-2xl bg-primary-500/10 border border-primary-200/50 text-center">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">Explore the AllChinaBuy Spreadsheet</h3>
                <p className="text-foreground-600 mb-6 text-sm">Browse curated product finds and start shopping smarter.</p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-block px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors"
                >
                  {t("heroCtaPrimary")}
                </a>
              </div>

              <div className="p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">Related Articles</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Link to="/article/is-allchinabuy-legit" className="p-4 rounded-xl bg-background-50 border border-background-200/50 hover:border-primary-300 transition-all">
                    <h4 className="font-semibold text-sm text-foreground-900 hover:text-primary-700 transition-colors">{t("articleTitle2")}</h4>
                  </Link>
                  <Link to="/article/allchinabuy-vs-superbuy" className="p-4 rounded-xl bg-background-50 border border-background-200/50 hover:border-primary-300 transition-all">
                    <h4 className="font-semibold text-sm text-foreground-900 hover:text-primary-700 transition-colors">{t("articleTitle3")}</h4>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}